import type { QuoteSettings } from '@/types/organization'
import { QueryClient, useMutation, useQueryClient } from '@tanstack/react-query'
import type { GraphQLResult } from 'aws-amplify/api'
import {
  OrganizationDefaults,
  OrganizationMember,
  OrganizationMemberInput,
  Team,
  UpdateInvitProps,
  UserTeam,
} from '@skop-app/lib-domain/types'

import { getAmplifyClient } from '@/services/amplifyClient'
import { useRouter } from '@/navigation'
import {
  acceptOrganizationInvitationMutation,
  assignSeatMutation,
  createOrganizationMutation,
  createOrgMemberMtn,
  reassignSeatMutation,
  updateOrganizationMemberMutation,
  updateOrganizationMutation,
} from '@/services/graphql/team'
import { useUserContext } from '@/context'
import { useSessionRuntime } from '@/context/SessionRuntimeContext'
import { useSelectedTeam } from '@/features/team-selection'
import { organizationKeys } from '@/services/queries/team'
import { subscriptionEntitlementKeys } from '@/services/queries/subscriptionEntitlements'
import type { Language } from '@/types/translations'
import type { SeatAwareOrganizationMember } from '@/types/seatAccess'
import { hasEffectiveSeatAccess } from '@/utils/seatAccess'
import { cleanInput, recursivileyGetItems } from '@/utils'
import { serializeOrganizationMetadata } from '@/utils/organizationMetadata'
import {
  createTeamWithRecovery,
  resumeTeamCreation,
} from '@/services/teamCreationRecovery'

import {
  type CurrentUserQueryParams,
  getEffectiveCurrentUserId,
  invalidateCurrentUserQueries,
  reconcileCurrentUserOrganizationMembership,
  upsertCurrentUserOrganizationMembership,
} from '../user/user.cache'

const client = getAmplifyClient()

export type OrganizationMemberRemovalInput = {
  memberId: string
  memberUserId?: string
  organizationID: string
  organizationMasterID?: string
  isCurrentUser: boolean
}

export type OrganizationMutationInput = Omit<
  OrganizationDefaults,
  'languages' | 'logo'
> & {
  quoteSettings?: QuoteSettings | null
  languages?: OrganizationDefaults['languages'] | Language[] | null
  logo?: string | null
}

const ORGANIZATION_LANGUAGE_CODES = new Set<Language>([
  'FR',
  'NL',
  'EN',
  'DE',
  'IT',
  'ES',
])

const normalizeOrganizationLanguages = (value: unknown): Language[] => {
  if (!Array.isArray(value)) return []
  const languages: Language[] = []
  for (const entry of value) {
    const raw =
      typeof entry === 'string'
        ? entry
        : entry && typeof entry === 'object' && 'value' in entry
          ? entry.value
          : undefined
    if (typeof raw !== 'string') continue
    const code = raw.trim().toUpperCase() as Language
    if (ORGANIZATION_LANGUAGE_CODES.has(code) && !languages.includes(code)) {
      languages.push(code)
    }
  }
  return languages
}

export type CreateOrganizationVariables = {
  organization: OrganizationMutationInput
  organizationMember: OrganizationMember | OrganizationMemberInput
  assignSeat?: boolean
}

type AcceptOrganizationInvitationResult = {
  acceptOrganizationInvitation?: {
    id: string
    organizationID?: string | null
    status?: string | null
    seatAccessState?: SeatAwareOrganizationMember['seatAccessState']
    onSeat?: boolean | null
    seatAccessReason?: string | null
    seatGraceStartedAt?: string | null
    seatGraceEndsAt?: string | null
    seatLicenseOrganizationID?: string | null
  } | null
}

const buildOrganizationMutationInput = (
  organization: Partial<OrganizationMutationInput> & { id: string },
) => {
  const input = { ...organization }

  if ('languages' in input) {
    const languages = normalizeOrganizationLanguages(input.languages)
    if (
      languages.length ||
      (Array.isArray(input.languages) && input.languages.length === 0)
    )
      input.languages = languages
    else delete input.languages
  }

  if (Array.isArray(input.metadata)) {
    input.metadata = serializeOrganizationMetadata(input.metadata)
  }

  return input
}

export type ProjectMemberRemovalInput = {
  memberId: string
  memberUserId?: string
  projectID: string
}

export type DeleteMemberVariables =
  | ProjectMemberRemovalInput
  | OrganizationMemberRemovalInput

type QueryCacheMemberInput = {
  id: string
  email?: string
  name?: string
  organizationID?: string
  organizationMasterID?: string
  projectID?: string
  status?: string
  userID?: string
}

const getOrganizationTreeQueryKey = ({
  organizationID,
  organizationMasterID,
}: {
  organizationID?: string
  organizationMasterID?: string
}) => organizationKeys.tree(organizationMasterID || organizationID)

const getProjectQueryKey = (projectID: string) =>
  ['project', projectID] as const

const invalidateOrganizationEntitlements = (queryClient: QueryClient) =>
  queryClient.invalidateQueries({
    queryKey: subscriptionEntitlementKeys.all,
  })

const buildArchivedOrganizationMemberInput = ({
  memberId,
  organizationID,
  organizationMasterID,
}: OrganizationMemberRemovalInput): QueryCacheMemberInput => ({
  id: memberId,
  status: 'ARCHIVED',
  organizationID,
  organizationMasterID,
})

const removeMemberFromMembersCache = ({
  memberId,
  organizationID,
  queryClient,
}: {
  memberId: string
  organizationID: string
  queryClient: QueryClient
}) => {
  queryClient.setQueryData(
    organizationKeys.members(organizationID),
    (oldData: OrganizationMember[] | undefined) =>
      oldData?.filter((member) => member.id !== memberId) ?? [],
  )
}

const removeMemberFromProjectCache = ({
  memberId,
  projectID,
  queryClient,
}: {
  memberId: string
  projectID: string
  queryClient: QueryClient
}) => {
  queryClient.setQueryData(getProjectQueryKey(projectID), (oldData: Team) => {
    if (!oldData) return oldData

    return {
      ...oldData,
      members: oldData.members.filter((member) => member.id !== memberId),
    }
  })
}

type UseMutateInviteOptions = {
  redirectOnAccept?: boolean
}

export const useMutateInvite = (options?: UseMutateInviteOptions) => {
  const { redirectOnAccept = true } = options ?? {}
  const queryClient = useQueryClient()
  const { setSelectedTeam } = useSelectedTeam()
  const { currentUserId, impersonatedUserId, sessionScopeKey } =
    useSessionRuntime()
  const effectiveUserId = getEffectiveCurrentUserId({
    sessionScopeKey,
    currentUserId,
    impersonatedUserId,
  })
  const router = useRouter()
  return useMutation({
    mutationKey: ['acceptOrganizationInvitation'],
    mutationFn: acceptOrganizationInvitationRequest,
    onSuccess: async (data, variables: UpdateInvitProps, _context) => {
      const acceptedOrganizationId =
        data.data?.acceptOrganizationInvitation?.organizationID ??
        variables.organizationID

      if (
        variables.status === 'DONE' &&
        data.data?.acceptOrganizationInvitation?.status === 'DONE' &&
        hasEffectiveSeatAccess(
          data.data.acceptOrganizationInvitation as SeatAwareOrganizationMember,
        ) &&
        acceptedOrganizationId
      ) {
        const selected = await setSelectedTeam(acceptedOrganizationId)
        if (selected && redirectOnAccept) router.push('/')
      }
      invalidateCurrentUserQueries(queryClient, {
        sessionScopeKey,
        currentUserId,
        impersonatedUserId,
      })
      if (effectiveUserId) {
        queryClient.invalidateQueries({
          queryKey: ['notifications', effectiveUserId],
        })
      }
      if (acceptedOrganizationId) {
        queryClient.invalidateQueries({
          queryKey: organizationKeys.summary(acceptedOrganizationId),
        })
        invalidateOrganizationEntitlements(queryClient)
      }
    },
  })
}

export const buildCreatedOrganizationMembership = ({
  organization,
  organizationMember,
}: CreateOrganizationVariables): UserTeam =>
  ({
    id: organizationMember.id,
    value: organization.id,
    label: organization.name,
    role: organizationMember.role,
    status: organizationMember.status ?? 'DONE',
    organizationStatus: organization.status ?? 'ACTIVE',
    color: organization.color,
    logo: organization.logo,
    masterID: organization.masterID,
    parentID: organization.parentID,
    isMaster: organization.isMaster,
    partner: organization.partner,
  }) as UserTeam

const selectCreatedOrganization = async (
  queryClient: QueryClient,
  currentUserQueryParams: CurrentUserQueryParams,
  setSelectedTeam: ReturnType<typeof useSelectedTeam>['setSelectedTeam'],
  membership: UserTeam,
) => {
  upsertCurrentUserOrganizationMembership(
    queryClient,
    currentUserQueryParams,
    membership,
  )
  const selected = await setSelectedTeam(membership.value)
  if (!selected) throw new Error("La sélection de l'équipe a échoué")
  const reconciled = await reconcileCurrentUserOrganizationMembership(
    queryClient,
    currentUserQueryParams,
    membership,
  )
  if (!reconciled) {
    throw new Error("L'appartenance à l'équipe n'a pas été confirmée")
  }
}

export type ReassignSeatVariables = {
  organizationID: string
  sourceMemberID: string
  targetMemberID: string
}

type ReassignSeatResult = {
  source: SeatAwareOrganizationMember
  target: SeatAwareOrganizationMember
}

export async function reassignSeatRequest({
  sourceMemberID,
  targetMemberID,
}: ReassignSeatVariables): Promise<ReassignSeatResult> {
  const result = (await client.graphql({
    query: reassignSeatMutation,
    variables: { input: { sourceMemberID, targetMemberID } },
  })) as GraphQLResult<{ reassignSeat?: ReassignSeatResult | null }>
  if (result.errors?.length) {
    throw Object.assign(new Error('Seat reassignment failed'), {
      errors: result.errors,
    })
  }
  const reassignment = result.data?.reassignSeat
  if (
    reassignment?.source.id !== sourceMemberID ||
    reassignment?.target.id !== targetMemberID ||
    reassignment.source.onSeat !== false ||
    reassignment.target.onSeat !== true ||
    reassignment.target.seatAccessState !== 'PAID'
  ) {
    throw new Error('Seat reassignment response could not be confirmed')
  }
  return reassignment
}

export const useReassignSeat = () => {
  const queryClient = useQueryClient()
  const { organizationScope } = useUserContext()
  const { setSolo } = useSelectedTeam()
  const { currentUserId, impersonatedUserId, sessionScopeKey } =
    useSessionRuntime()
  const effectiveUserId = getEffectiveCurrentUserId({
    sessionScopeKey,
    currentUserId,
    impersonatedUserId,
  })
  return useMutation({
    mutationKey: ['reassignSeat'],
    mutationFn: (variables: ReassignSeatVariables) => {
      if (variables.organizationID !== organizationScope.activeOrganizationId)
        throw Object.assign(new Error('Organization scope changed'), {
          name: 'Unauthorized',
        })
      return reassignSeatRequest(variables)
    },
    retry: false,
    onSuccess: (result) => {
      if (result.source.userID === effectiveUserId) void setSolo()
    },
    onSettled: async (_data, _error, variables) => {
      if (variables) {
        await queryClient.invalidateQueries({
          queryKey: organizationKeys.members(variables.organizationID),
        })
        queryClient.invalidateQueries({
          queryKey: organizationKeys.summary(variables.organizationID),
        })
        queryClient.invalidateQueries({
          queryKey: organizationKeys.tree(variables.organizationID),
        })
      }
      invalidateOrganizationEntitlements(queryClient)
      invalidateCurrentUserQueries(queryClient, {
        sessionScopeKey,
        currentUserId,
        impersonatedUserId,
      })
    },
  })
}

export type AssignAvailableSeatVariables = {
  memberID: string
  organizationID: string
}

export async function assignAvailableSeatRequest({
  memberID,
  organizationID,
}: AssignAvailableSeatVariables) {
  const result = (await client.graphql({
    query: assignSeatMutation,
    variables: {
      input: {
        id: memberID,
        organizationID,
        status: 'DONE',
        allowPaidExpansion: false,
      },
    },
  })) as GraphQLResult<{ assignSeat?: SeatAwareOrganizationMember | null }>
  if (result.errors?.length)
    throw Object.assign(new Error('Seat assignment failed'), {
      errors: result.errors,
    })
  const member = result.data?.assignSeat
  if (
    member?.id !== memberID ||
    member.onSeat !== true ||
    member.seatAccessState !== 'PAID'
  )
    throw new Error('Seat assignment response could not be confirmed')
  return member
}

export const useAssignAvailableSeat = () => {
  const queryClient = useQueryClient()
  const { organizationScope } = useUserContext()
  const { currentUserId, impersonatedUserId, sessionScopeKey } =
    useSessionRuntime()
  return useMutation({
    mutationKey: ['assignAvailableSeat'],
    mutationFn: (variables: AssignAvailableSeatVariables) => {
      if (variables.organizationID !== organizationScope.activeOrganizationId)
        throw Object.assign(new Error('Organization scope changed'), {
          name: 'Unauthorized',
        })
      return assignAvailableSeatRequest(variables)
    },
    retry: false,
    onSettled: async (_data, _error, variables) => {
      if (variables) {
        await queryClient.invalidateQueries({
          queryKey: organizationKeys.members(variables.organizationID),
        })
        queryClient.invalidateQueries({
          queryKey: organizationKeys.summary(variables.organizationID),
        })
      }
      invalidateOrganizationEntitlements(queryClient)
      invalidateCurrentUserQueries(queryClient, {
        sessionScopeKey,
        currentUserId,
        impersonatedUserId,
      })
    },
  })
}

export const useCreateOrganization = () => {
  const queryClient = useQueryClient()
  const { setSelectedTeam } = useSelectedTeam()
  const { currentUserId, impersonatedUserId, sessionScopeKey } =
    useSessionRuntime()
  return useMutation({
    mutationKey: ['createOrganization'],
    mutationFn: createOrganization,
    onSuccess: async (_data, variables, _context) => {
      invalidateOrganizationEntitlements(queryClient)
      const currentUserQueryParams = {
        sessionScopeKey,
        currentUserId,
        impersonatedUserId,
      }

      if (variables.organization?.parentID) {
        invalidateCurrentUserQueries(queryClient, currentUserQueryParams)
        queryClient.invalidateQueries({
          queryKey: organizationKeys.tree(variables.organization.parentID),
        })
        queryClient.invalidateQueries({
          queryKey: organizationKeys.summary(variables.organization.parentID),
        })
        return
      }

      queryClient.invalidateQueries({
        queryKey: organizationKeys.summary(variables.organization.id),
      })

      await selectCreatedOrganization(
        queryClient,
        currentUserQueryParams,
        setSelectedTeam,
        buildCreatedOrganizationMembership(variables),
      )
    },
  })
}

export const useCreateOrganizationWithRecovery = () => {
  const queryClient = useQueryClient()
  const { setSelectedTeam } = useSelectedTeam()
  const { currentUserId, impersonatedUserId, sessionScopeKey } =
    useSessionRuntime()

  return useMutation({
    mutationKey: ['createOrganizationWithRecovery'],
    mutationFn: (variables: Parameters<typeof createTeamWithRecovery>[0]) =>
      createTeamWithRecovery(
        {
          ...variables,
          organization: buildOrganizationMutationInput({
            ...variables.organization,
            status: variables.organization.status ?? 'ACTIVE',
          }) as OrganizationMutationInput,
        },
        () =>
          selectCreatedOrganization(
            queryClient,
            { sessionScopeKey, currentUserId, impersonatedUserId },
            setSelectedTeam,
            buildCreatedOrganizationMembership(variables),
          ),
      ),
    onSuccess: (_data, variables) => {
      invalidateOrganizationEntitlements(queryClient)
      queryClient.invalidateQueries({
        queryKey: organizationKeys.summary(variables.organization.id),
      })
    },
  })
}

export const useResumeOrganizationCreation = () => {
  const queryClient = useQueryClient()
  const { setSelectedTeam } = useSelectedTeam()
  const { currentUserId, impersonatedUserId, sessionScopeKey } =
    useSessionRuntime()

  return useMutation({
    mutationKey: ['resumeOrganizationCreation'],
    mutationFn: (variables: Parameters<typeof resumeTeamCreation>[0]) =>
      resumeTeamCreation(variables, (attempt) =>
        selectCreatedOrganization(
          queryClient,
          { sessionScopeKey, currentUserId, impersonatedUserId },
          setSelectedTeam,
          {
            id: attempt.memberId,
            value: attempt.organizationId,
            label: attempt.organizationName,
            role: 'ADMIN',
            status: 'DONE',
            organizationStatus: 'ACTIVE',
          } as UserTeam,
        ),
      ),
    onSuccess: (organizationId) => {
      invalidateOrganizationEntitlements(queryClient)
      queryClient.invalidateQueries({
        queryKey: organizationKeys.summary(organizationId),
      })
    },
  })
}

export const useUpdateOrganization = () => {
  const queryClient = useQueryClient()
  const { organizationScope } = useUserContext()
  const { currentUserId, impersonatedUserId, sessionScopeKey } =
    useSessionRuntime()

  const resolveExistingOrganizationName = (
    organizationId: string,
  ): string | undefined => {
    if (organizationScope?.selectedOrganization?.id === organizationId) {
      const scopedName = organizationScope.selectedOrganization?.name
      if (scopedName) return scopedName
    }
    return (
      queryClient.getQueryData<Team>(organizationKeys.summary(organizationId))
        ?.name ?? undefined
    )
  }

  return useMutation({
    mutationKey: ['updateOrganization'],
    mutationFn: (variables: Parameters<typeof updateOrganization>[0]) => {
      const { organization } = variables
      if (organization.name == null) {
        const existingName = resolveExistingOrganizationName(organization.id)
        if (existingName) {
          return updateOrganization({
            organization: { ...organization, name: existingName },
          })
        }
      }
      return updateOrganization(variables)
    },
    onSuccess: (_data, variables, _context) => {
      const cachedSummary = queryClient.getQueryData<Team | undefined>(
        organizationKeys.summary(variables.organization.id),
      )
      const relatedOrganizationIds = [
        variables.organization.id,
        variables.organization.parentID,
        variables.organization.masterID,
        cachedSummary?.parentID,
        cachedSummary?.masterID,
      ].filter((id): id is string => Boolean(id))

      for (const organizationId of new Set(relatedOrganizationIds)) {
        queryClient.invalidateQueries({
          queryKey: organizationKeys.summary(organizationId),
        })
        queryClient.invalidateQueries({
          queryKey: organizationKeys.tree(organizationId),
        })
      }
      queryClient.invalidateQueries({
        queryKey: organizationKeys.members(variables.organization.id),
      })
      queryClient.invalidateQueries({
        queryKey: organizationKeys.metadataScope(variables.organization.id),
      })
      invalidateOrganizationEntitlements(queryClient)
      invalidateCurrentUserQueries(queryClient, {
        sessionScopeKey,
        currentUserId,
        impersonatedUserId,
      })
    },
  })
}

export const useArchiveOrganizationMember = () => {
  const queryClient = useQueryClient()
  const { organizationScope } = useUserContext()
  const { setSolo } = useSelectedTeam()
  const { currentUserId, impersonatedUserId, sessionScopeKey } =
    useSessionRuntime()
  return useMutation({
    mutationKey: ['archiveOrganizationMember'],
    mutationFn: archiveOrganizationMember,
    onMutate(variables) {
      const queryKey = getOrganizationTreeQueryKey(variables)
      const archivedInput = buildArchivedOrganizationMemberInput(variables)

      queryClient.cancelQueries({
        queryKey: organizationKeys.members(variables.organizationID),
      })
      queryClient.cancelQueries({
        queryKey,
      })

      removeMemberFromMembersCache({
        memberId: variables.memberId,
        organizationID: variables.organizationID,
        queryClient,
      })
      updateQueryCache(archivedInput, queryClient, queryKey)
    },
    onSuccess: (_data, variables) => {
      if (
        variables.isCurrentUser &&
        variables.organizationID === organizationScope.activeOrganizationId
      ) {
        void setSolo()
      }
    },
    onSettled: (_data, _errors, variables) => {
      if (!variables) return

      queryClient.invalidateQueries({
        queryKey: organizationKeys.members(variables.organizationID),
      })

      queryClient.invalidateQueries({
        queryKey: getOrganizationTreeQueryKey(variables),
      })
      invalidateOrganizationEntitlements(queryClient)

      if (variables.isCurrentUser) {
        invalidateCurrentUserQueries(queryClient, {
          sessionScopeKey,
          currentUserId,
          impersonatedUserId,
        })
      }
    },
  })
}

export const useDeleteProjectMember = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationKey: ['deleteProjectMember'],
    mutationFn: deleteProjectMember,
    onMutate(variables) {
      const queryKey = getProjectQueryKey(variables.projectID)

      queryClient.cancelQueries({
        queryKey,
      })

      removeMemberFromProjectCache({
        memberId: variables.memberId,
        projectID: variables.projectID,
        queryClient,
      })
    },
    onSettled: (_data, _errors, variables) => {
      if (!variables) return

      queryClient.invalidateQueries({
        queryKey: getProjectQueryKey(variables.projectID),
      })
    },
  })
}

const deleteMemberMutationFn = async (variables: DeleteMemberVariables) => {
  if ('projectID' in variables && variables.projectID) {
    return deleteProjectMember(variables)
  }

  return archiveOrganizationMember(variables as OrganizationMemberRemovalInput)
}

export const useDeleteMember = () => {
  const queryClient = useQueryClient()
  const { organizationScope } = useUserContext()
  const { setSolo } = useSelectedTeam()
  const { currentUserId, impersonatedUserId, sessionScopeKey } =
    useSessionRuntime()
  return useMutation({
    mutationKey: ['deleteMember'],
    mutationFn: deleteMemberMutationFn,
    onMutate(variables) {
      if ('projectID' in variables && variables.projectID) {
        const projectVariables = variables as ProjectMemberRemovalInput
        const queryKey = getProjectQueryKey(projectVariables.projectID)

        queryClient.cancelQueries({
          queryKey,
        })

        removeMemberFromProjectCache({
          memberId: projectVariables.memberId,
          projectID: projectVariables.projectID,
          queryClient,
        })
        return
      }

      const orgVariables = variables as OrganizationMemberRemovalInput
      const queryKey = getOrganizationTreeQueryKey(orgVariables)
      const archivedInput = buildArchivedOrganizationMemberInput(orgVariables)

      queryClient.cancelQueries({
        queryKey: organizationKeys.members(orgVariables.organizationID),
      })
      queryClient.cancelQueries({
        queryKey,
      })

      removeMemberFromMembersCache({
        memberId: orgVariables.memberId,
        organizationID: orgVariables.organizationID,
        queryClient,
      })
      updateQueryCache(archivedInput, queryClient, queryKey)
    },
    onSuccess: (_data, variables) => {
      if ('projectID' in variables && variables.projectID) {
        return
      }

      const orgVariables = variables as OrganizationMemberRemovalInput
      if (
        orgVariables.isCurrentUser &&
        orgVariables.organizationID === organizationScope.activeOrganizationId
      ) {
        void setSolo()
      }
    },
    onSettled: (_data, _errors, variables) => {
      if (!variables) return

      if ('projectID' in variables && variables.projectID) {
        queryClient.invalidateQueries({
          queryKey: getProjectQueryKey(
            (variables as ProjectMemberRemovalInput).projectID,
          ),
        })
        return
      }

      const orgVariables = variables as OrganizationMemberRemovalInput
      queryClient.invalidateQueries({
        queryKey: organizationKeys.members(orgVariables.organizationID),
      })

      queryClient.invalidateQueries({
        queryKey: getOrganizationTreeQueryKey(orgVariables),
      })
      invalidateOrganizationEntitlements(queryClient)

      if (orgVariables.isCurrentUser) {
        invalidateCurrentUserQueries(queryClient, {
          sessionScopeKey,
          currentUserId,
          impersonatedUserId,
        })
      }
    },
  })
}

export const useCreateOrganizationMember = () => {
  const queryClient = useQueryClient()
  const { currentUserId, impersonatedUserId, sessionScopeKey } =
    useSessionRuntime()
  return useMutation({
    mutationKey: ['createOrganizationMember'],
    mutationFn: createOrganizationMember,
    onMutate(variables) {
      const isProject = variables.projectID
      const queryKey = isProject
        ? ['project', variables.projectID]
        : organizationKeys.tree(
            variables.organizationMasterID || variables.organizationID,
          )
      updateQueryCache(variables, queryClient, queryKey)
    },
    onSettled: (_data, _error, variables) => {
      if (!variables?.organizationID) return

      queryClient.invalidateQueries({
        queryKey: organizationKeys.members(variables.organizationID),
      })
      queryClient.invalidateQueries({
        queryKey: organizationKeys.tree(
          variables.organizationMasterID || variables.organizationID,
        ),
      })
      if (!variables.projectID && variables.type !== 'PROJECT') {
        invalidateOrganizationEntitlements(queryClient)
      }
      invalidateCurrentUserQueries(queryClient, {
        sessionScopeKey,
        currentUserId,
        impersonatedUserId,
      })
    },
  })
}

export type UpdateMemberSeatVariables = {
  member: Pick<
    OrganizationMember,
    'id' | 'userID' | 'email' | 'organizationID' | 'role' | 'status' | 'type'
  >
  onSeat: boolean
}

const updateMemberSeatRequest = async ({
  member,
  onSeat,
}: UpdateMemberSeatVariables) => {
  if (onSeat) {
    // assignSeat is the quota-aware path: the backend rejects with
    // SEAT_LIMIT_EXCEEDED when every subscription seat is taken.
    return await client.graphql({
      query: assignSeatMutation,
      variables: {
        input: {
          id: member.id,
          userID: member.userID,
          email: member.email,
          organizationID: member.organizationID,
          role: member.role,
          status: member.status,
          type: member.type,
          onSeat: true,
        },
      },
    })
  }

  return await updateOrgRequest({
    id: member.id,
    organizationID: member.organizationID,
    onSeat: false,
  })
}

export const useUpdateMemberSeat = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationKey: ['updateMemberSeat'],
    mutationFn: updateMemberSeatRequest,
    onMutate: async ({ member, onSeat }: UpdateMemberSeatVariables) => {
      if (!member.organizationID) return undefined
      const queryKey = organizationKeys.members(member.organizationID)
      await queryClient.cancelQueries({ queryKey })
      const previousMembers =
        queryClient.getQueryData<OrganizationMember[]>(queryKey)
      queryClient.setQueryData<OrganizationMember[]>(queryKey, (members) =>
        members?.map((item) =>
          item.id === member.id ? { ...item, onSeat } : item,
        ),
      )
      return { previousMembers, queryKey }
    },
    onError: (_error, _variables, context) => {
      if (context?.previousMembers) {
        queryClient.setQueryData(context.queryKey, context.previousMembers)
      }
    },
    onSettled: (_data, _error, variables) => {
      if (!variables?.member.organizationID) return
      queryClient.invalidateQueries({
        queryKey: organizationKeys.members(variables.member.organizationID),
      })
    },
  })
}

export const useUpdateOrganizationMember = () => {
  const queryClient = useQueryClient()
  const { currentUserId, impersonatedUserId, sessionScopeKey } =
    useSessionRuntime()
  return useMutation({
    mutationKey: ['updateOrganizationMember'],
    mutationFn: updateOrgRequest,
    onMutate(variables) {
      const isProject = variables.projectID

      const queryKey = isProject
        ? ['project', variables.projectID]
        : organizationKeys.tree(
            variables.organizationMasterID || variables.organizationID,
          )

      updateQueryCache(variables, queryClient, queryKey)
    },
    onSettled: (_data, _error, variables) => {
      if (!variables?.organizationID) return

      queryClient.invalidateQueries({
        queryKey: organizationKeys.members(variables.organizationID),
      })
      queryClient.invalidateQueries({
        queryKey: organizationKeys.tree(
          variables.organizationMasterID || variables.organizationID,
        ),
      })
      if (!variables.projectID && variables.type !== 'PROJECT') {
        invalidateOrganizationEntitlements(queryClient)
      }
      invalidateCurrentUserQueries(queryClient, {
        sessionScopeKey,
        currentUserId,
        impersonatedUserId,
      })
    },
  })
}

const updateQueryCache = (
  variables: QueryCacheMemberInput,
  queryClient: QueryClient,
  queryKey: readonly unknown[],
) => {
  const queryData = queryClient.getQueryData(queryKey) as Team
  if (!queryData) return

  // Member should be on top level of the organization
  if (
    queryData.id === variables.organizationID ||
    queryData.id === variables.projectID
  ) {
    // Is the member already in the team, update it, if not, add it
    const member = queryData.members.find(
      (member) => member.id === variables.id,
    )

    if (member) {
      queryClient.setQueryData(queryKey, (oldData: Team) => {
        return {
          ...oldData,
          members: updateOrRemoveMember(variables, oldData.members),
        }
      })
    } else {
      queryClient.setQueryData(queryKey, (oldData: Team) => {
        return {
          ...oldData,
          members: [
            ...oldData.members,
            {
              ...variables,
              member: {
                email: variables.email,
                name: variables.name,
                id: variables.userID,
              },
            },
          ],
        }
      })
    }
  }
  // Member should be on a subteam
  else {
    // check if the member is already in a subteam
    const teams = queryData.teams.length
      ? recursivileyGetItems(queryData.teams, 'teams')
      : []
    const team = teams.find((team) => team.id === variables.organizationID)

    if (team) {
      const member = team.members.find((member) => member.id === variables.id)
      if (member) {
        queryClient.setQueryData(queryKey, (oldData: Team) => {
          return {
            ...oldData,
            teams: oldData.teams.map((team) => {
              if (team.id === variables.organizationID) {
                return {
                  ...team,
                  members: updateOrRemoveMember(variables, team.members),
                }
              }
              return team
            }),
          }
        })
      } else {
        queryClient.setQueryData(queryKey, (oldData: Team) => {
          return {
            ...oldData,
            teams: oldData.teams.map((team) => {
              if (team.id === variables.organizationID) {
                return {
                  ...team,
                  members: [
                    ...team.members,
                    {
                      ...variables,
                      member: {
                        email: variables.email,
                        name: variables.name,
                        id: variables.userID,
                      },
                    },
                  ],
                }
              }
              return team
            }),
          }
        })
      }
    }
  }
}

const updateOrRemoveMember = (variables, members) => {
  return variables.status === 'ARCHIVED'
    ? members.filter((member) => member.id !== variables.id)
    : members.map((item) => {
        if (item.id === variables.id) {
          return {
            ...item,
            ...variables,
          }
        }
        return item
      })
}

export const updateOrgRequest = async (input) => {
  return await client.graphql({
    query: updateOrganizationMemberMutation,
    variables: {
      input,
    },
  })
}

export const acceptOrganizationInvitationRequest = async ({
  id,
  status,
}: UpdateInvitProps) => {
  const result = (await client.graphql({
    query: acceptOrganizationInvitationMutation,
    variables: {
      input: {
        id,
        status,
      },
    },
  })) as GraphQLResult<AcceptOrganizationInvitationResult>

  if (result.errors?.length) {
    throw Object.assign(
      new Error(
        result.errors.map(({ message }) => message).join('; ') ||
          'Invitation update failed',
      ),
      { errors: result.errors },
    )
  }

  return result
}

const createOrganizationMember = async (
  variables: OrganizationMemberInput & { organizationName?: string },
) => {
  // only used to update the cache
  delete variables.organizationName
  delete variables.name

  if (shouldAssignSeat(variables)) {
    return await client.graphql({
      query: assignSeatMutation,
      variables: { input: variables },
    })
  }

  return await client.graphql({
    query: createOrgMemberMtn,
    variables: { input: variables },
  })
}

const shouldAssignSeat = (member: OrganizationMemberInput) => {
  if (member.projectID || member.type === 'PROJECT') return false
  return member.status === 'DONE' && member.onSeat !== false
}

const archiveOrganizationMember = async ({
  memberId,
  organizationID,
  organizationMasterID,
}: OrganizationMemberRemovalInput) => {
  return await updateOrgRequest({
    id: memberId,
    status: 'ARCHIVED',
    organizationID,
    organizationMasterID,
  })
}

const deleteProjectMember = async ({ memberId }: ProjectMemberRemovalInput) => {
  return await updateOrgRequest({
    id: memberId,
    status: 'ARCHIVED',
  })
}

const createOrganization = async ({
  organization,
  organizationMember,
  assignSeat = true,
}: CreateOrganizationVariables) => {
  const organizationWithStatus = {
    ...organization,
    status: organization.status ?? 'ACTIVE',
  }

  await client.graphql({
    query: createOrganizationMutation,
    variables: {
      input: buildOrganizationMutationInput(organizationWithStatus),
    },
  })

  if (assignSeat) {
    await client.graphql({
      query: assignSeatMutation,
      variables: { input: { ...organizationMember, onSeat: true } },
    })
    return
  }

  // Without seat intent the founder joins unseated; only assignSeat may claim a
  // seat, since it is the quota-aware path.
  await client.graphql({
    query: createOrgMemberMtn,
    variables: { input: { ...organizationMember, onSeat: false } },
  })
}

const updateOrganization = async ({
  organization,
}: {
  organization: Partial<OrganizationMutationInput> & { id: string }
}) => {
  const keysToRemove = ['propertyId']
  const input = cleanInput({
    input: buildOrganizationMutationInput(organization),
    keysToRemove,
  })
  return await client.graphql({
    query: updateOrganizationMutation,
    variables: { input },
  })
}
